import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  ErrorScreen,
  FlexDiv,
  LoaderComponent,
  Message,
  MessageInput,
} from "../../components/";
import { useReservationDetails } from "../../hooks/index";
import {
  Header,
  StyledThreadScreen,
  InputWrapper,
  StyledTab,
  SkeletonMessage,
} from "./styles";
import {
  useThreads,
  useScrollToEnd,
  useSendMessage,
  useUpdateReservation,
} from "../../hooks/";
import assets from "../../assets/index";
import { API_URL, paths } from "../../config/endpoints";
import { Key, useCallback } from "react";
import { Reservation } from "@/types/reservation";
import React from "react";

const InboxPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    reservation,
    loading: reservationLoading,
    updateReservation,
  } = useReservationDetails(id || "");

  const getThreadUid = useCallback((reservation: Reservation | null) => {
    const threads = reservation?.reservationMessageThreads;
    return threads && threads.length > 0 ? threads[0].uid : undefined;
  }, []);

  const threadUid = getThreadUid(reservation!);
  const apiUrl = `${API_URL}/${paths.guest}/reservations-conversation-threads/${threadUid}/messages`;
  const apiSingleReservationUrl = `${API_URL}/${paths.guest}/${paths.reservations}/${id}`;

  const { threads, setThreads } = useThreads(reservation!, threadUid);
  const messagesEndRef = useScrollToEnd(threads);
  const {
    sendMessage,
    sendMessageError,
    loading: isMessageLoading,
    setLoading,
  } = useSendMessage(threadUid, setThreads);

  const { updateReservationDetails } = useUpdateReservation(
    apiSingleReservationUrl,
    updateReservation
  );

  const handleSendMessage = async (message: string) => {
    setLoading(true); // Start loading before sending the message
    await sendMessage(message, apiUrl, updateReservationDetails);
    setLoading(false); // Stop loading once the message has been sent
  };

  if (reservationLoading)
    return <LoaderComponent content="Loading messages..." />;

  if (!reservation?.reservationMessageThreads)
    return (
      <ErrorScreen
        content={
          "Looks like there is some error when fetching data. Please try again later. "
        }
        imageSrc={assets.NoResultsVector}
      />
    );

  return (
    <StyledThreadScreen>
      <Header>
        <FlexDiv
          alignitems="center"
          justifycontent="space-between"
          width="100%"
        >
          {reservation.confirmed ? (
            <StyledTab confirmed={true}>Confirmed</StyledTab>
          ) : (
            <StyledTab confirmed={false}>Not confirmed</StyledTab>
          )}
        </FlexDiv>
        <FlexDiv
          alignitems="center"
          gap="6px"
          justifycontent="flex-start"
          width="100%"
        >
          <img
            className="go-back"
            src={assets.GoBack}
            alt="go back"
            height={14}
            onClick={() => navigate("/inbox")}
          />
          <h2>{reservation.unitType?.name}</h2>{" "}
          <Button
            onClick={() => navigate(`/reservation/${reservation.uid}`)}
            style={{
              width: "fit-content",
              padding: "6px 8px",
              height: "fit-content",
              borderRadius: "15px",
              marginLeft: "6px",
            }}
          >
            See Reservation
          </Button>
        </FlexDiv>
      </Header>
      <div className="messages-display">
        {threads.map(
          (thread: {
            uid: Key | null | undefined;
            messages: {
              uid: Key | null | undefined;
              incoming: any;
              body: string | undefined;
            }[];
          }) => (
            <React.Fragment key={thread.uid}>
              {thread.messages.map(
                (message: {
                  uid: Key | null | undefined;
                  incoming: any;
                  body: string | undefined;
                }) => (
                  <Message
                    key={message.uid}
                    variant={message.incoming ? "incoming" : "outgoing"}
                    messageContent={message.body}
                  />
                )
              )}
            </React.Fragment>
          )
        )}
        <div ref={messagesEndRef} />
        {/* Show Skeleton only while sending a message */}
        {isMessageLoading && <SkeletonMessage />}
        <div ref={messagesEndRef} />
      </div>
      <InputWrapper>
        <MessageInput
          threadUid={threadUid}
          handleSendMessage={handleSendMessage}
          error={sendMessageError}
        />
      </InputWrapper>
    </StyledThreadScreen>
  );
};

export default InboxPage;
